import axios from 'axios';
import { useFormik } from 'formik';
import _, { debounce } from 'lodash';
import React, { useCallback, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { DateInput } from 'semantic-ui-calendar-react';
import { Button, Container, Form, Grid, Icon, Input, Label, Modal, Popup } from 'semantic-ui-react';
import { addToast } from '../../../../../store/actions/ToastActions';

function Plate() {
	const dispatch = useDispatch();
	const [plate, setPlate] = useState('');
	const [loading, setLoading] = useState(false);
	const [data, setData] = useState([]);
	const [restoring, setRestoring] = useState(false);
	const [open, setOpen] = useState(false);
	const email = useSelector(state => state.auth.email);

	const formik = useFormik({
		initialValues: {
			pool: '',
			date: '',
			well: ''
		},
		onSubmit: (values, { setSubmitting, resetForm }) => {
			if (values.pool) {
				axios
					.post('/api/orma/update', {
						pool: values.pool,
						date: values.date,
						well: values.well,
						plate,
						email
					})
					.then(() => {
						dispatch(
							addToast({
								type: 'success',
								message: 'ORMA pool has been successfully added.'
							})
						);
						const newWells = [...data];
						const index = _.findIndex(data, { well: values.well });
						if (index > -1) {
							newWells[index].pool = values.pool;
							newWells[index].date = values.date;
						}
						setData(newWells);
					})
					.catch(err => {
						dispatch(
							addToast({
								type: 'error',
								message: err.message
							})
						);
					})
					.finally(() => {
						setSubmitting(false);
						setOpen(false);
						resetForm();
					});
			} else {
				axios
					.post('/api/orma/delete', {
						well: values.well,
						plate
					})
					.then(() => {
						dispatch(
							addToast({
								type: 'success',
								message: 'Wolbachia pool has been successfully removed.'
							})
						);
						const newWells = [...data];
						const index = _.findIndex(data, { well: values.well });
						if (index > -1) {
							newWells[index].pool = '';
							newWells[index].date = '';
						}
						setData(newWells);
					})
					.catch(err => {
						dispatch(
							addToast({
								type: 'error',
								message: err.message
							})
						);
					})
					.finally(() => {
						setSubmitting(false);
						setOpen(false);
					});
			}
		}
	});

	const fetchPlateData = async value => {
		try {
			const response = await axios.post('/api/orma/read', {
				value
			});

			setData(response.data);
		} catch (error) {
			console.error('Error fetching data:', error);
		} finally {
			setLoading(false);
		}
	};

	const debouncedChangeHandler = useCallback(
		debounce(value => {
			if (value === '') {
				setPlate('');
				setData([]); // Clear data if input is blank
				setLoading(false);
			} else {
				setPlate(value);
				fetchPlateData(value); // Fetch data here
			}
		}, 500),
		[]
	);

	const handleChange = e => {
		setLoading(true); // Start loading when typing
		debouncedChangeHandler(e.target.value);
	};

	const handleWell = well => {
		const current = _.find(data, { well });
		formik.setFieldValue('pool', current.pool || '');
		formik.setFieldValue('date', current.date || new Date().toLocaleDateString());
		formik.setFieldValue('well', current.well);
		setOpen(true);
	};

	const handleRestore = async () => {
		if (plate) {
			setRestoring(true);

			await axios
				.post('/api/orma/restore', {
					plate
				})
				.then(() => {
					setData([]);
					setPlate('');
				})
				.catch(err => console.error(err))
				.finally(() => setRestoring(false));
		}
	};

	return (
		<>
			<Modal size="mini" open={open} onClose={() => setOpen(false)}>
				<Modal.Content>
					<p className="strong">Pool</p>
					<p className="quiet">Scan the pool number and add it to the selected plate well</p>
					<Form>
						<DateInput
							name="date"
							value={formik.values.date}
							onChange={(event, { value }) => formik.setFieldValue('date', value)}
							onBlur={() => formik.setFieldTouched('date')}
							placeholder="mm/dd/yyyy"
							dateFormat="L"
							hideMobileKeyboard
							animation="fade"
							closable
							iconPosition="left"
							autoComplete="off"
							clearable
							clearIcon={<Icon name="remove" />}
						/>
						<Input
							name="pool"
							size="small"
							fluid
							placeholder="Pool number..."
							value={formik.values.pool}
							onChange={e => formik.setFieldValue('pool', e.target.value)}
						/>
					</Form>
					<p className="x-small quiet mt-10">Clear the input and submit if you like to remove the pool from the well</p>
				</Modal.Content>
				<Modal.Actions>
					<Button size="mini" onClick={() => setOpen(false)}>
						Cancel
					</Button>
					<Button
						type="submit"
						color="blue"
						size="mini"
						loading={formik.isSubmitting}
						onClick={() => formik.submitForm()}
					>
						Submit
					</Button>
				</Modal.Actions>
			</Modal>
			<Container>
				<Grid stackable padded="vertically">
					<Grid.Row columns={1} centered>
						<Grid.Column computer={5} tablet={8}>
							<Form size="small">
								<Form.Field
									icon="search"
									size="small"
									loading={loading}
									placeholder="Plate Number.."
									control={Input}
									onChange={handleChange}
								/>
							</Form>
						</Grid.Column>
					</Grid.Row>
					<Grid.Row columns={1} centered>
						<Grid.Column>
							<div className="pre">
								<div className="pre-header x-small p-10 strong">Plate</div>
								{data.length ? (
									<Grid stackable celled="internally">
										<Grid.Row columns={1}>
											<Grid.Column textAlign="right">
												<Popup
													header="Warning!!"
													content="Deleting the plate it's irreversible!!"
													size="mini"
													position="top right"
													trigger={
														<Button
															primary
															size="mini"
															content="Delete"
															loading={restoring}
															onClick={handleRestore}
															disabled={!plate}
														/>
													}
												/>
											</Grid.Column>
										</Grid.Row>
										<Grid.Row only="computer" columns={12}>
											{_.map(_.orderBy(data, ['index'], ['asc']), item => {
												return (
													<Grid.Column key={item.well} textAlign="center" verticalAlign="middle">
														<div className="x-small truncate quiet">
															<Label circular size="mini" empty color={item.pool ? 'green' : 'yellow'} />
														</div>
														<div
															aria-label="Add Pool Button"
															role="button"
															tabIndex={-1}
															className="pool-button"
															onClick={() => handleWell(item.well)}
														>
															{item.well}
														</div>
														<div className="x-small truncate quiet">{item.pool ? item.pool : 'Edit'}</div>
													</Grid.Column>
												);
											})}
										</Grid.Row>
										<Grid.Row only="tablet" columns={6}>
											{_.map(_.orderBy(data, ['index'], ['asc']), item => {
												return (
													<Grid.Column key={item.well} textAlign="center" verticalAlign="middle">
														<div className="x-small truncate quiet">
															<Label circular size="mini" empty color={item.pool ? 'green' : 'yellow'} />
														</div>
														<div
															aria-label="Add Pool Button"
															role="button"
															tabIndex={-1}
															className="pool-button"
															onClick={() => handleWell(item.well)}
														>
															{item.well}
														</div>
														<div className="x-small truncate quiet">{item.pool ? item.pool : 'Edit'}</div>
													</Grid.Column>
												);
											})}
										</Grid.Row>
										<Grid.Row only="mobile" columns={3}>
											{_.map(_.orderBy(data, ['index'], ['asc']), item => {
												return (
													<Grid.Column key={item.well} textAlign="center" verticalAlign="middle">
														<div className="x-small truncate quiet">
															<Label circular size="mini" empty color={item.pool ? 'green' : 'yellow'} />
														</div>
														<div
															aria-label="Add Pool Button"
															role="button"
															tabIndex={-1}
															className="pool-button"
															onClick={() => handleWell(item.well)}
														>
															{item.well}
														</div>
														<div className="x-small truncate quiet">{item.pool ? item.pool : 'Edit'}</div>
													</Grid.Column>
												);
											})}
										</Grid.Row>
									</Grid>
								) : (
									<>
										{loading ? (
											<div className="pre-loading x-small p-10 strong">Fetching wells from database...</div>
										) : (
											<div className="x-small p-10 strong">Enter plate number on the input field to load pools</div>
										)}
										<div className="placeholder" />
										<div className="placeholder full-width" />
										<div className="placeholder full-width" />
										<div className="placeholder full-width" />
										<div className="divider" />
										<div className="placeholder" />
										<div className="placeholder full-width" />
										<div className="placeholder full-width" />
										<div className="placeholder full-width" />
										<div className="divider" />
										<div className="placeholder" />
										<div className="placeholder full-width" />
										<div className="placeholder full-width" />
										<div className="placeholder full-width" />
										<div className="divider" />
										<div className="placeholder" />
										<div className="placeholder full-width" />
										<div className="placeholder full-width" />
										<div className="placeholder full-width" />
										<div className="divider" />
										<div className="placeholder" />
										<div className="placeholder full-width" />
									</>
								)}
							</div>
						</Grid.Column>
					</Grid.Row>
				</Grid>
			</Container>
		</>
	);
}

export default Plate;
